import React, { useState, useEffect } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Typography,
  Box,
  Collapse,
  IconButton,
  Alert,
  Tooltip,
  Zoom
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Close as CloseIcon,
  Send as SendIcon,
  BugReport as BugIcon,
  LightbulbOutlined as FeatureIcon,
  Comment as GeneralIcon,
  Calculate as CalculationIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useUser } from '../../context/UserContext';
import { submitFeedback, FEEDBACK_TYPES, FEEDBACK_RATINGS, validateFeedbackData } from '../../services/feedback';
import { trackEvent, getCurrentPage } from '../../services/analytics';

// Feedback type icons mapping
const feedbackTypeIcons = {
  [FEEDBACK_TYPES.BUG]: <BugIcon />,
  [FEEDBACK_TYPES.FEATURE]: <FeatureIcon />,
  [FEEDBACK_TYPES.GENERAL]: <GeneralIcon />,
  [FEEDBACK_TYPES.CALCULATION]: <CalculationIcon />
};

// Feedback type labels
const feedbackTypeLabels = {
  [FEEDBACK_TYPES.BUG]: 'Bug Report',
  [FEEDBACK_TYPES.FEATURE]: 'Feature Request',
  [FEEDBACK_TYPES.GENERAL]: 'General Feedback',
  [FEEDBACK_TYPES.CALCULATION]: 'Calculation Issue',
  [FEEDBACK_TYPES.UI_UX]: 'User Interface',
  [FEEDBACK_TYPES.PERFORMANCE]: 'Performance Issue'
};

const FeedbackButton = ({ 
  position = { bottom: 24, right: 24 },
  size = 'large',
  color = 'primary',
  minimized = false 
}) => {
  const [open, setOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [formData, setFormData] = useState({
    feedbackType: '',
    rating: 0,
    message: '',
    userEmail: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showEmailField, setShowEmailField] = useState(false);

  const { user } = useUser();

  // Auto-fill email if user is logged in
  useEffect(() => {
    if (user && user.email) {
      setFormData(prev => ({
        ...prev,
        userEmail: user.email
      }));
    }
  }, [user]);

  // Track feedback button visibility (only run once on mount)
  useEffect(() => {
    trackEvent('feedback_button_shown', {
      page: getCurrentPage(),
      position: `${position.bottom}_${position.right}`
    });
  }, []); // Empty dependency array - only run on mount

  const handleOpen = () => {
    setOpen(true);
    setIsMinimized(false);
    trackEvent('feedback_button_clicked', {
      page: getCurrentPage()
    });
  };

  const handleClose = () => {
    setOpen(false);
    setSubmitStatus(null);
    setErrors([]);
    
    // Track close without submission if form has data
    if (formData.message || formData.feedbackType) {
      trackEvent('feedback_dialog_closed', {
        page: getCurrentPage(),
        has_content: true,
        feedback_type: formData.feedbackType
      });
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleRatingChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      rating: newValue
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors([]);

    // Validate form data
    const validation = validateFeedbackData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      setSubmitting(false);
      return;
    }

    try {
      const result = await submitFeedback({
        ...formData,
        userId: user?.uid || null,
        userEmail: formData.userEmail || user?.email || null
      });

      if (result.success) {
        setSubmitStatus('success');
        trackEvent('feedback_submitted', {
          feedback_type: formData.feedbackType,
          rating: formData.rating,
          has_email: !!(formData.userEmail || user?.email),
          message_length: formData.message.length
        });
        
        // Reset form after short delay
        setTimeout(() => {
          setFormData({
            feedbackType: '',
            rating: 0,
            message: '',
            userEmail: user?.email || ''
          });
          setOpen(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
        setErrors([result.error || 'Failed to submit feedback']);
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors(['An unexpected error occurred']);
      console.error('[FeedbackButton] Submit error:', error);
    }

    setSubmitting(false);
  };

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
    trackEvent('feedback_button_minimized', {
      minimized: !isMinimized
    });
  };

  const fabStyle = {
    position: 'fixed',
    bottom: position.bottom,
    right: position.right,
    zIndex: 1000,
    transition: 'all 0.3s ease'
  };

  return (
    <>
      {/* Floating Action Button */}
      <Zoom in={true}>
        <Tooltip title="Send Feedback" placement="left">
          <Fab
            color={color}
            size={size}
            onClick={handleOpen}
            style={fabStyle}
            aria-label="Send feedback"
          >
            <FeedbackIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      {/* Feedback Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 12
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Send Feedback
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Help us improve the FERS Navigator! Your feedback is valuable.
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Success/Error Messages */}
          {submitStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Thank you! Your feedback has been submitted successfully.
            </Alert>
          )}

          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.length > 0 ? errors.join(', ') : 'Failed to submit feedback'}
            </Alert>
          )}

          {/* Feedback Type Selection */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Feedback Type</InputLabel>
            <Select
              value={formData.feedbackType}
              onChange={handleInputChange('feedbackType')}
              label="Feedback Type"
            >
              {Object.entries(feedbackTypeLabels).map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {feedbackTypeIcons[type]}
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Rating */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography component="legend" gutterBottom>
              Overall Experience (Optional)
            </Typography>
            <Rating
              value={formData.rating}
              onChange={handleRatingChange}
              size="large"
              precision={1}
            />
          </Box>

          {/* Message */}
          <TextField
            autoFocus
            margin="normal"
            label="Your Feedback"
            multiline
            rows={4}
            fullWidth
            required
            value={formData.message}
            onChange={handleInputChange('message')}
            placeholder="Please describe your feedback in detail..."
            helperText={`${formData.message.length}/2000 characters`}
            inputProps={{ maxLength: 2000 }}
          />

          {/* Email Field (Collapsible) */}
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={showEmailField ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowEmailField(!showEmailField)}
              variant="text"
              size="small"
            >
              {user?.email ? 'Change Email' : 'Add Email for Follow-up'}
            </Button>
            
            <Collapse in={showEmailField}>
              <TextField
                margin="normal"
                label="Email (Optional)"
                type="email"
                fullWidth
                value={formData.userEmail}
                onChange={handleInputChange('userEmail')}
                placeholder="your.email@example.com"
                helperText="We'll only use this to follow up on your feedback"
              />
            </Collapse>
          </Box>

          {/* Error Display */}
          {errors.length > 0 && submitStatus !== 'error' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.message || !formData.feedbackType}
            startIcon={<SendIcon />}
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackButton;